<template lang="pug">
  .auth--login
    form.auth__form(@submit.prevent="checkForm")
      //- Email input
      input.no-border-bottom(
        type="email"
        name="email"
        v-model="email"
        v-validate="'required|email'"
        :data-vv-as="$t('auth.form.email')"
        :class="{'error': errors.has('email') }"
        :placeholder="$t('auth.form.email')")

      //- Password input
      Password(
        name="password"
        v-model="password"
        v-validate="'required'"
        :password="password"
        :data-vv-as="$t('auth.form.password')"
        :error="errors.has('password')")

      //- Notificaciones comerciales
      .checkbox.margin-halve-top
        input(
          type="checkbox"
          name="remember"
          v-model="remember")

        label(for="remember")
          | {{ $t('auth.form.remember') }}

      //- Errores de validación del formulario
      .margin-halve-top
        .auth__error(v-show="errors.has('email')") {{ errors.first('email') }}
        .auth__error(v-show="errors.has('password')") {{ errors.first('password') }}

      //- Botón enviar
      van-button(
        size="large"
        type="primary"
        :disabled="isLoading"
        native-type="submit") {{ $t('auth.form.send') }}

    hr.auth__hr

    //- Botón Demo
    van-button.margin-bottom(
      size="large"
      type="gray"
      @click="setDemoMode") {{ $t('auth.demo')}}

    //- Enlaces a otras páginas
    van-row
      van-col(span="12")
        router-link.auth__link(to="/auth/forgot") {{ $t('auth.forgot') }}
      van-col(span="12")
        router-link.auth__link(to="/auth/register") {{ $t('auth.signUpLink') }}

    //- Botones de Modo
    .auth__modes(v-if="!isProduction")
      van-button.auth__modes__button(@click="setAppMode('DEV')" :type="appMode === 'DEV' ? 'warning' : 'gray'" block) DEV
      van-button.auth__modes__button(@click="setAppMode('PRE')" :type="appMode === 'PRE' ? 'warning' : 'gray'" block) PRE
      van-button.auth__modes__button(@click="setAppMode('PROD')" :type="appMode === 'production' ? 'warning' : 'gray'" block) PROD


    //- Popup para que el usuario compruebe si su dispositivo es compatible con esta aplicación
    van-popup.unit__webserver__popup(v-model="showWebserver")
      .padding-double
        img(src="@/assets/img/webserver.png")

      .text--center.padding-left.padding-right
        h1 {{ $t('auth.messages.device.title') }}
        p {{ $t('auth.messages.device.description') }}

      .popup__button.popup__button--confirm(@click="hideWebserver")
        | {{ $t('auth.messages.device.close') }}
</template>

<script>
import i18n from '@/locales/i18n';
import { mapGetters, mapActions } from 'vuex';
import { IS_PRODUCTION, CONNECT, CONFIG } from '@/app.config';
import ApiService from '@/services/api.service';
import UserService from '@/services/user.service';
import DemoService from '@/services/demo.service';
import Password from '@/components/Password.vue';
import CryptoService from '@/services/crypto.service';
import { getErrorId } from '@/services/error.service';
import { consoleInfo } from '@/utils/console.utils';

export default {
  components: { Password },
  data() {
    return {
      email: '',
      password: '',
      remember: true,
      showWebserver: false,
      authError: null,
      isLoading: false,
      isProduction: IS_PRODUCTION,
    };
  },

  computed: {
    ...mapGetters({
      appMode: 'globalModule/getAppMode',
    }),
  },

  methods: {
    ...mapActions({
      setAppModeStore: 'globalModule/setAppMode',
    }),

    /**
     * Cambia el modo de la aplicación
     */
    setAppMode(mode) {
      ApiService.setBaseURL(CONNECT[mode].API_URL, CONNECT.API_VERSION);
      this.setAppModeStore(mode);
    },

    /**
     * Compruebo si los datos del formulario son válidos
     */
    checkForm() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.loginUser();
        }
      });
    },

    /**
     * Intento loguear al usuario
     */
    async loginUser() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.authError = null;

        // Obtengo los datos del usuario y conecto los sockets
        await UserService.getUserData(this.email, this.password);

        if (this.remember) {
          window.localStorage.setItem('userEmail', this.email);
          window.localStorage.setItem('userPassword', CryptoService.encrypt(this.password));
        } else {
          window.localStorage.setItem('userEmail', '');
          window.localStorage.setItem('userPassword', '');
        }

        this.$toast.clear();
        this.$router.push(this.$route.query.redirect || { name: 'home' });
      } catch (error) {
        const authError = getErrorId(error);
        this.$toast.clear();

        switch (authError) {
          case 'userNotConfirmed':
            // El usuario no ha validado su email, muestro una ventana preguntando
            // si quiere volver a recibir el email de validación.
            this.$dialog
              .confirm({
                title: i18n.t(`auth.messages.notValidate.title`),
                message: i18n.t(`auth.messages.notValidate.message`),
              })
              .then(() => this.sendConfirmEmail());
            break;
          case 'userNotExist':
            // El usuario no existe en la base de datos,
            // muestro un mensaje de error
            this.$toast.fail({
              mask: true,
              message: i18n.t('auth.messages.userNotExist'),
            });
            break;
          default:
            // Ocurrió cualquier otro error que impide al usuario loguearse
            this.$toast.fail({
              mask: true,
              message: i18n.t('general.errors.default.message'),
            });
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Vuelve a enviar el email de confirmación al usuario
     */
    async sendConfirmEmail() {
      try {
        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Realizo la petición
        await UserService.sendConfirmEmail(this.email);

        // Quito el loader y Confirmo el envío del email
        this.$toast.clear();
        this.$toast.success({
          mask: true,
          message: i18n.t('auth.messages.resendToken'),
        });
      } catch (error) {
        // Ocurrió algún error,
        // quito el loader y muestro un mensaje de error
        this.$toast.clear();
        this.$toast.fail({
          mask: true,
          message: i18n.t('general.errors.default.message'),
        });
      }
    },

    /*
     * Hide webserver notification
     */
    hideWebserver() {
      this.showWebserver = false;
      localStorage.setItem('showedInfoWebserver', true);
    },

    /*
     * Demo mode
     */
    async setDemoMode() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.authError = null;

        await DemoService.getUserData();

        this.$toast.clear();
        this.$router.push({ name: 'home' });
      } catch (error) {
        this.authError = getErrorId(error);
        this.$toast.clear();

        this.$toast.fail({
          mask: true,
          message: i18n.t('general.errors.default.message'),
        });
      }
    },

    /**
     * Show redirect reason message
     */
    showRedirectReason(reason) {
      consoleInfo('Login redirect reason', this.$route.query.reaseon);

      if (reason === 'logout') {
        this.$toast.success({ mask: true, message: i18n.t('auth.messages.logout') });
      } else if (reason === 'deleteAccount') {
        this.$toast.success({ mask: true, message: i18n.t('auth.messages.deleteAccount') });
      } else if (reason !== 'notPermission' && i18n.te(`auth.messages.${reason}`)) {
        this.$toast.fail({
          mask: true,
          duration: 4000,
          message: i18n.t(`auth.messages.${reason}`),
        });
      }
    },

    /**
     * Obtiene el email y la contraseña del usuario si se había logueado previamente
     */
    getStoreData() {
      const userEmail = window.localStorage.getItem('userEmail');
      const userPassword = window.localStorage.getItem('userPassword');

      this.email = userEmail || '';
      this.password = userPassword ? CryptoService.decrypt(userPassword) : '';
    },
  },

  created() {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera

    if (this.$route.query.reaseon) {
      this.showRedirectReason(this.$route.query.reaseon);
    }

    if (CONFIG.INIT_MESSAGE) {
      const showedInfoWebserver = localStorage.getItem('showedInfoWebserver');
      this.showWebserver = !showedInfoWebserver;
    }

    this.getStoreData();
  },

  destroyed() {
    window.scrollTo(0, 0);
  },
};
</script>
